import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SmartAlert } from "@/hooks/use-smart-alerts";
import { AlertTriangle, TrendingUp, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface SmartAlertsWidgetProps {
  alerts: SmartAlert[];
  isLoading?: boolean;
  onDismiss?: (alertId: string) => void;
}

export function SmartAlertsWidget({ alerts, isLoading, onDismiss }: SmartAlertsWidgetProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Alertas Inteligentes</CardTitle>
          <CardDescription>Análise automática dos seus gastos</CardDescription>
        </CardHeader>
        <CardContent className="h-[100px] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Analisando...</div>
        </CardContent>
      </Card>
    );
  }

  const visibleAlerts = alerts.filter(a => !a.isDismissed).slice(0, 3);

  if (visibleAlerts.length === 0) {
    return (
      <Card className="border-success/30 bg-success/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Info className="h-5 w-5 text-success" />
            Tudo em ordem!
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Não detectamos padrões de gastos incomuns. Continue assim!
          </p>
        </CardContent>
      </Card>
    );
  }

  const getSeverityStyles = (severity: SmartAlert["severity"]) => {
    switch (severity) {
      case "critical":
        return {
          bg: "bg-destructive/10 border-destructive/30",
          icon: "text-destructive",
          badge: "bg-destructive text-destructive-foreground",
        };
      case "warning":
        return {
          bg: "bg-warning/10 border-warning/30",
          icon: "text-warning",
          badge: "bg-warning text-warning-foreground",
        };
      default:
        return {
          bg: "bg-info/10 border-info/30",
          icon: "text-info",
          badge: "bg-info text-info-foreground",
        };
    }
  };

  const getSeverityIcon = (severity: SmartAlert["severity"]) => {
    switch (severity) {
      case "critical":
        return <AlertTriangle className="h-4 w-4" />;
      case "warning":
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" />
          Alertas Inteligentes
        </CardTitle>
        <CardDescription>Análise automática dos seus gastos</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {visibleAlerts.map((alert) => {
          const styles = getSeverityStyles(alert.severity);
          
          return (
            <div
              key={alert.id}
              className={cn(
                "relative p-3 rounded-lg border",
                styles.bg
              )}
            >
              <div className="flex items-start gap-3 pr-6">
                <div className={cn("mt-0.5", styles.icon)}>
                  {getSeverityIcon(alert.severity)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-medium">{alert.title}</h4>
                    {alert.categoryColor && (
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: alert.categoryColor }}
                      />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {alert.description}
                  </p>
                </div>
              </div>
              {onDismiss && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6 hover:bg-background/50"
                  onClick={() => onDismiss(alert.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
